<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue';
import ReportFilter from "@/components/features/reports/ReportFilter.vue";
import YarnStockValueReportDialog from "@/components/features/reports/YarnStockValueReportDialog.vue";
import { getMergeStockValue } from '@/api/reports';
import { ref, type Ref, computed } from "vue";
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import type { YarnStockValueResponse, YarnStockValueParams } from '@/types/report-types';

const toast = useToast();

const selectedDate = ref<Date | null>(null);

const reportData: Ref<YarnStockValueResponse | null> = ref(null);
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const showReportDialog = ref(false);

const dummyOptions = computed(() => [{ label: 'Stock Value', value: 'stock' }]);
const selectedDummy = ref<string | null>('stock');

const handleGenerateReport = async () => {
  if (!selectedDate.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select a date.', life: 3000 });
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;
  reportData.value = null;

  try {
    const params: YarnStockValueParams = {
      date: selectedDate.value.toISOString(),
    };

    const data = await getMergeStockValue(params);

    if (data && data.mergeStockValues) {
      reportData.value = data;
      if (data.mergeStockValues.length > 0) {
        showReportDialog.value = true;
      } else {
        toast.add({ severity: 'info', summary: 'No Data', detail: 'No stock value data found for the selected date.', life: 4000 });
        showReportDialog.value = false;
      }
    } else {
      reportData.value = null;
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No stock value data found for the selected date (empty response).', life: 4000 });
      showReportDialog.value = false;
    }

  } catch (error: any) {
    console.error("Error fetching Yarn Stock Value report:", error);
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || "Failed to fetch report data.";
    errorMessage.value = apiErrorMessage;
    toast.add({ severity: 'error', summary: 'API Error', detail: apiErrorMessage, life: 5000 });
    showReportDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handleCloseReportDialog = () => {
  showReportDialog.value = false;
};
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <ReportFilter
          title="Yarn Stock Value Report"
          dropdownLabel="Report Type"
          v-model="selectedDummy"
          :dropdownOptions="dummyOptions"
          :disabled="false"
          placeholder="Stock Value Report"
          :showCurrencyFilter="false"
          :singleDateMode="true"
          @update:startDate="selectedDate = $event"
          @generate-report="handleGenerateReport"
      />

      <div v-if="isLoading" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s"/>
        <p class="ml-3 text-lg text-gray-600">Generating Report...</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div v-if="!isLoading && !reportData && !errorMessage && !showReportDialog" class="mt-10 text-center text-gray-500">
        <p>Select a date and click "Create Report" to view yarn stock value details.</p>
      </div>

      <YarnStockValueReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :selectedDate="selectedDate"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout>
</template>
